/**
 * Go AI Player
 *
 * Three difficulty levels:
 * - Easy: random valid move, 500ms delay
 * - Medium: heuristic — prioritize captures, avoid self-atari,
 *           prefer center/corners, 1s delay
 * - Hard: simplified Monte Carlo Tree Search (MCTS) with
 *         random playouts, 100-500 iterations, 2s delay
 */

import {
  GoColor,
  GoBoard,
  GoCell,
  GoPosition,
  GoDifficulty,
  GoBoardSize,
  DIRECTIONS,
} from './types';

export class GoAI {
  private difficulty: GoDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: GoDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  getDelay(): number {
    switch (this.difficulty) {
      case 'easy': return 500;
      case 'medium': return 1000;
      case 'hard': return 2000;
      default: return 1000;
    }
  }

  setDifficulty(difficulty: GoDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): GoDifficulty {
    return this.difficulty;
  }

  /**
   * Choose the best move for the given player.
   * Returns null if AI decides to pass.
   */
  chooseMove(
    board: GoBoard,
    player: GoColor,
    boardSize: GoBoardSize,
    koPoint: GoPosition | null
  ): GoPosition | null {
    const validMoves = this.getValidMoves(board, player, boardSize, koPoint);
    if (validMoves.length === 0) return null;

    switch (this.difficulty) {
      case 'easy':
        return this.chooseMoveEasy(validMoves);
      case 'medium':
        return this.chooseMoveMedium(board, player, boardSize, validMoves);
      case 'hard':
        return this.chooseMoveHard(board, player, boardSize, koPoint, validMoves);
      default:
        return this.chooseMoveEasy(validMoves);
    }
  }

  /**
   * Schedule an AI move after the configured delay.
   */
  scheduleMove(
    board: GoBoard,
    player: GoColor,
    boardSize: GoBoardSize,
    koPoint: GoPosition | null,
    onMove: (position: GoPosition | null) => void
  ): () => void {
    this.cancelPendingMove();

    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      const move = this.chooseMove(board, player, boardSize, koPoint);
      onMove(move);
    }, delay);

    return () => this.cancelPendingMove();
  }

  cancelPendingMove(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  destroy(): void {
    this.cancelPendingMove();
  }

  // =========================================================================
  // Easy: Random valid move
  // =========================================================================

  private chooseMoveEasy(validMoves: GoPosition[]): GoPosition {
    const index = Math.floor(Math.random() * validMoves.length);
    return validMoves[index];
  }

  // =========================================================================
  // Medium: Heuristic-based
  // =========================================================================

  private chooseMoveMedium(
    board: GoBoard,
    player: GoColor,
    boardSize: GoBoardSize,
    validMoves: GoPosition[]
  ): GoPosition {
    const opponent = player === 'black' ? 'white' : 'black';
    const scored: { move: GoPosition; score: number }[] = [];

    for (const move of validMoves) {
      let score = 0;

      // 1. Prioritize captures
      const captures = this.countCaptures(board, player, move, boardSize);
      score += captures * 20;

      // 2. Avoid self-atari (placing a stone that leaves own group with 1 liberty)
      const testBoard = this.applyMove(board, player, move, boardSize);
      const selfGroup = this.getGroup(testBoard, move.row, move.col, boardSize);
      const selfLiberties = this.countGroupLiberties(testBoard, selfGroup, boardSize);
      if (selfLiberties === 1 && selfGroup.length === 1) {
        score -= 15; // Penalize self-atari for single stones
      }

      // 3. Prefer moves near the center
      const center = (boardSize - 1) / 2;
      const distFromCenter = Math.abs(move.row - center) + Math.abs(move.col - center);
      score += Math.max(0, boardSize - distFromCenter);

      // 4. Prefer third and fourth lines (good for territory)
      const rowLine = Math.min(move.row, boardSize - 1 - move.row);
      const colLine = Math.min(move.col, boardSize - 1 - move.col);
      if (rowLine >= 2 && rowLine <= 3) score += 3;
      if (colLine >= 2 && colLine <= 3) score += 3;

      // 5. Bonus for threatening opponent groups with few liberties
      for (const [dr, dc] of DIRECTIONS) {
        const nr = move.row + dr;
        const nc = move.col + dc;
        if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && board[nr][nc] === opponent) {
          const oppGroup = this.getGroup(board, nr, nc, boardSize);
          const oppLiberties = this.countGroupLiberties(board, oppGroup, boardSize);
          if (oppLiberties <= 2) score += 10;
          if (oppLiberties <= 3) score += 3;
        }
      }

      // 6. Bonus for extending own groups
      for (const [dr, dc] of DIRECTIONS) {
        const nr = move.row + dr;
        const nc = move.col + dc;
        if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && board[nr][nc] === player) {
          score += 2;
        }
      }

      scored.push({ move, score });
    }

    // Sort by score descending and pick from top candidates with some randomness
    scored.sort((a, b) => b.score - a.score);
    const topCount = Math.min(3, scored.length);
    const topIndex = Math.floor(Math.random() * topCount);
    return scored[topIndex].move;
  }

  // =========================================================================
  // Hard: Simplified Monte Carlo Tree Search
  // =========================================================================

  private chooseMoveHard(
    board: GoBoard,
    player: GoColor,
    boardSize: GoBoardSize,
    koPoint: GoPosition | null,
    validMoves: GoPosition[]
  ): GoPosition {
    const iterations = boardSize <= 9 ? 500 : boardSize <= 13 ? 300 : 100;
    const wins: Map<string, number> = new Map();
    const visits: Map<string, number> = new Map();

    for (const move of validMoves) {
      const key = `${move.row},${move.col}`;
      wins.set(key, 0);
      visits.set(key, 0);
    }

    for (let i = 0; i < iterations; i++) {
      // Pick a move to explore (UCB1-like selection)
      const totalVisits = i + 1;
      let bestMove = validMoves[0];
      let bestUCB = -Infinity;

      for (const move of validMoves) {
        const key = `${move.row},${move.col}`;
        const w = wins.get(key) || 0;
        const v = visits.get(key) || 0;

        let ucb: number;
        if (v === 0) {
          ucb = Infinity; // Explore unvisited moves first
        } else {
          ucb = w / v + Math.sqrt(2 * Math.log(totalVisits) / v);
        }

        if (ucb > bestUCB) {
          bestUCB = ucb;
          bestMove = move;
        }
      }

      // Simulate a random playout from this move
      const testBoard = this.applyMove(board, player, bestMove, boardSize);
      const result = this.randomPlayout(testBoard, this.getOpponent(player), boardSize);

      const key = `${bestMove.row},${bestMove.col}`;
      visits.set(key, (visits.get(key) || 0) + 1);
      if (result === player) {
        wins.set(key, (wins.get(key) || 0) + 1);
      }
    }

    // Pick the move with the highest win rate
    let bestMove = validMoves[0];
    let bestWinRate = -1;

    for (const move of validMoves) {
      const key = `${move.row},${move.col}`;
      const v = visits.get(key) || 0;
      const w = wins.get(key) || 0;
      const winRate = v > 0 ? w / v : 0;

      if (winRate > bestWinRate) {
        bestWinRate = winRate;
        bestMove = move;
      }
    }

    return bestMove;
  }

  /**
   * Run a random playout from the given board state.
   * Returns the winner color.
   */
  private randomPlayout(
    board: GoBoard,
    currentPlayer: GoColor,
    boardSize: GoBoardSize
  ): GoColor {
    const maxMoves = boardSize * boardSize * 2;
    let player = currentPlayer;
    let passes = 0;
    const playBoard = board.map(row => [...row]);

    for (let i = 0; i < maxMoves && passes < 2; i++) {
      const moves = this.getValidMoves(playBoard, player, boardSize, null);

      if (moves.length === 0) {
        passes++;
        player = this.getOpponent(player);
        continue;
      }

      // Pick a random move, avoiding obvious self-atari
      const move = moves[Math.floor(Math.random() * moves.length)];

      // Apply the move
      playBoard[move.row][move.col] = player;

      // Capture opponent groups
      const opponent = this.getOpponent(player);
      for (const [dr, dc] of DIRECTIONS) {
        const nr = move.row + dr;
        const nc = move.col + dc;
        if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && playBoard[nr][nc] === opponent) {
          const group = this.getGroup(playBoard, nr, nc, boardSize);
          const liberties = this.countGroupLiberties(playBoard, group, boardSize);
          if (liberties === 0) {
            for (const pos of group) {
              playBoard[pos.row][pos.col] = null;
            }
          }
        }
      }

      passes = 0;
      player = this.getOpponent(player);
    }

    // Score the final position
    return this.scoreBoard(playBoard, boardSize);
  }

  /**
   * Simple area scoring for playout evaluation.
   */
  private scoreBoard(board: GoBoard, boardSize: number): GoColor {
    let blackScore = 0;
    let whiteScore = 6.5; // komi

    const visited = new Set<string>();

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (board[r][c] === 'black') {
          blackScore++;
        } else if (board[r][c] === 'white') {
          whiteScore++;
        } else {
          const key = `${r},${c}`;
          if (!visited.has(key)) {
            // Flood-fill empty region
            const region: GoPosition[] = [];
            const regionVisited = new Set<string>();
            const stack: GoPosition[] = [{ row: r, col: c }];
            let touchesBlack = false;
            let touchesWhite = false;

            while (stack.length > 0) {
              const pos = stack.pop()!;
              const pk = `${pos.row},${pos.col}`;
              if (regionVisited.has(pk)) continue;
              regionVisited.add(pk);

              if (pos.row < 0 || pos.row >= boardSize || pos.col < 0 || pos.col >= boardSize) continue;

              const cell = board[pos.row][pos.col];
              if (cell === 'black') { touchesBlack = true; continue; }
              if (cell === 'white') { touchesWhite = true; continue; }

              region.push(pos);
              visited.add(pk);

              for (const [dr, dc] of DIRECTIONS) {
                stack.push({ row: pos.row + dr, col: pos.col + dc });
              }
            }

            if (touchesBlack && !touchesWhite) blackScore += region.length;
            else if (touchesWhite && !touchesBlack) whiteScore += region.length;
          }
        }
      }
    }

    return blackScore > whiteScore ? 'black' : 'white';
  }

  // =========================================================================
  // Board Helpers
  // =========================================================================

  private getValidMoves(
    board: GoBoard,
    player: GoColor,
    boardSize: number,
    koPoint: GoPosition | null
  ): GoPosition[] {
    const moves: GoPosition[] = [];
    const opponent = this.getOpponent(player);

    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (board[row][col] !== null) continue;
        if (koPoint && koPoint.row === row && koPoint.col === col) continue;

        // Try placing
        const testBoard = board.map(r => [...r]);
        testBoard[row][col] = player;

        let captured = false;
        for (const [dr, dc] of DIRECTIONS) {
          const nr = row + dr;
          const nc = col + dc;
          if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && testBoard[nr][nc] === opponent) {
            const group = this.getGroup(testBoard, nr, nc, boardSize);
            const liberties = this.countGroupLiberties(testBoard, group, boardSize);
            if (liberties === 0) {
              captured = true;
              for (const pos of group) {
                testBoard[pos.row][pos.col] = null;
              }
            }
          }
        }

        if (!captured) {
          const selfGroup = this.getGroup(testBoard, row, col, boardSize);
          const selfLiberties = this.countGroupLiberties(testBoard, selfGroup, boardSize);
          if (selfLiberties === 0) continue;
        }

        moves.push({ row, col });
      }
    }

    return moves;
  }

  private countCaptures(
    board: GoBoard,
    player: GoColor,
    move: GoPosition,
    boardSize: number
  ): number {
    const opponent = this.getOpponent(player);
    const testBoard = board.map(r => [...r]);
    testBoard[move.row][move.col] = player;

    let captures = 0;
    for (const [dr, dc] of DIRECTIONS) {
      const nr = move.row + dr;
      const nc = move.col + dc;
      if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && testBoard[nr][nc] === opponent) {
        const group = this.getGroup(testBoard, nr, nc, boardSize);
        const liberties = this.countGroupLiberties(testBoard, group, boardSize);
        if (liberties === 0) {
          captures += group.length;
          for (const pos of group) {
            testBoard[pos.row][pos.col] = null;
          }
        }
      }
    }

    return captures;
  }

  private applyMove(
    board: GoBoard,
    player: GoColor,
    move: GoPosition,
    boardSize: number
  ): GoBoard {
    const newBoard = board.map(r => [...r]);
    newBoard[move.row][move.col] = player;

    const opponent = this.getOpponent(player);
    for (const [dr, dc] of DIRECTIONS) {
      const nr = move.row + dr;
      const nc = move.col + dc;
      if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && newBoard[nr][nc] === opponent) {
        const group = this.getGroup(newBoard, nr, nc, boardSize);
        const liberties = this.countGroupLiberties(newBoard, group, boardSize);
        if (liberties === 0) {
          for (const pos of group) {
            newBoard[pos.row][pos.col] = null;
          }
        }
      }
    }

    return newBoard;
  }

  private getGroup(board: GoBoard, row: number, col: number, boardSize: number): GoPosition[] {
    const color = board[row][col];
    if (color === null) return [];

    const group: GoPosition[] = [];
    const visited = new Set<string>();
    const stack: GoPosition[] = [{ row, col }];

    while (stack.length > 0) {
      const pos = stack.pop()!;
      const key = `${pos.row},${pos.col}`;
      if (visited.has(key)) continue;
      visited.add(key);

      if (pos.row < 0 || pos.row >= boardSize || pos.col < 0 || pos.col >= boardSize) continue;
      if (board[pos.row][pos.col] !== color) continue;

      group.push(pos);
      for (const [dr, dc] of DIRECTIONS) {
        stack.push({ row: pos.row + dr, col: pos.col + dc });
      }
    }

    return group;
  }

  private countGroupLiberties(board: GoBoard, group: GoPosition[], boardSize: number): number {
    const libertySet = new Set<string>();
    for (const pos of group) {
      for (const [dr, dc] of DIRECTIONS) {
        const nr = pos.row + dr;
        const nc = pos.col + dc;
        if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize && board[nr][nc] === null) {
          libertySet.add(`${nr},${nc}`);
        }
      }
    }
    return libertySet.size;
  }

  private getOpponent(player: GoColor): GoColor {
    return player === 'black' ? 'white' : 'black';
  }
}
